import { useCallback, useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';

interface HealthResponse {
  status: string;
  version?: string;
  runtime_mode?: string;
  database_kind?: string;
  uptime_seconds?: number;
}

interface DoctorCheck {
  name: string;
  status: string;
  detail: string;
}

interface DoctorResponse {
  healthy: boolean;
  summary: string;
  checks: DoctorCheck[];
}

function formatUptime(seconds?: number): string {
  if (typeof seconds !== 'number') return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function checkStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'ok':
    case 'pass':
      return 'text-[#39FF14]';
    case 'warn':
    case 'warning':
      return 'text-yellow-400';
    case 'fail':
    case 'error':
      return 'text-red-400';
    default:
      return 'text-gray-400';
  }
}

function checkStatusGlyph(status: string): string {
  switch (status.toLowerCase()) {
    case 'ok':
    case 'pass':
      return '✓';
    case 'warn':
    case 'warning':
      return '!';
    case 'fail':
    case 'error':
      return '✕';
    default:
      return '·';
  }
}

export function HealthTab() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [doctor, setDoctor] = useState<DoctorResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [healthResult, doctorResult] = await Promise.allSettled([
      engineFetchJson<HealthResponse>('/health'),
      engineFetchJson<DoctorResponse>('/harness/doctor'),
    ]);
    if (healthResult.status === 'fulfilled') {
      setHealth(healthResult.value);
    } else {
      setError(
        healthResult.reason instanceof Error
          ? healthResult.reason.message
          : 'Failed to load /api/v1/health',
      );
    }
    if (doctorResult.status === 'fulfilled') {
      setDoctor(doctorResult.value);
    }
    setLastChecked(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const connected = health?.status === 'ok';

  return (
    <div className="p-6 font-mono text-sm text-gray-200 space-y-4">
      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-wider text-gray-500">System health</div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {lastChecked && <span>checked {lastChecked.toLocaleTimeString()}</span>}
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="px-2 py-1 border border-gray-800 rounded hover:border-[#39FF14]/40 hover:text-[#39FF14] transition-colors disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-lg ${connected ? 'text-[#39FF14]' : 'text-red-400'}`}>●</span>
          <span className={`text-xs uppercase tracking-wider ${connected ? 'text-[#39FF14]' : 'text-red-400'}`}>
            {loading && !health ? 'Checking' : connected ? 'Engine connected' : 'Engine unreachable'}
          </span>
        </div>
        {error && !health && (
          <p className="text-xs text-red-400 mb-2">{error}</p>
        )}
        {health && (
          <div className="grid grid-cols-2 gap-3 text-xs">
            <InfoRow label="Status" value={health.status} />
            {health.version && <InfoRow label="Version" value={health.version} />}
            {health.runtime_mode && <InfoRow label="Runtime" value={health.runtime_mode} />}
            {health.database_kind && <InfoRow label="Database" value={health.database_kind} />}
            {typeof health.uptime_seconds === 'number' && (
              <InfoRow label="Uptime" value={formatUptime(health.uptime_seconds)} />
            )}
          </div>
        )}
      </div>

      <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Harness doctor</div>
        {loading && !doctor ? (
          <p className="text-gray-500 text-xs">Checking harness…</p>
        ) : !doctor ? (
          <p className="text-gray-500 text-xs">Harness doctor unavailable.</p>
        ) : (
          <>
            <div className={`text-xs mb-3 ${doctor.healthy ? 'text-[#39FF14]' : 'text-yellow-400'}`}>
              {doctor.summary}
            </div>
            <div className="space-y-1">
              {doctor.checks.map((c) => (
                <div key={c.name} className="flex items-start gap-2 text-xs">
                  <span className={`w-4 text-center ${checkStatusColor(c.status)}`}>
                    {checkStatusGlyph(c.status)}
                  </span>
                  <div className="flex-1">
                    <span className="text-gray-200">{c.name}</span>
                    <span className="text-gray-500 ml-2">{c.detail}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-200">{value}</span>
    </div>
  );
}
